import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { connect } from 'react-redux';
import {
  fetchCategories,
  catFetchData,
  channelsFetchData,
  catProgRowsFetchData,
} from '../actions';
import translate from './TranslateHOC';
import PageTitle from './PageTitle';
import SectionTitle from './SectionTitle';
import CardGrid from './CardGrid';
import {
  isGenreMatch,
  getSelectedChannel,
  decodeHtml,
  getCurrentCategoryName,
} from '../helpers';
import PageMeta from './PageMeta';
import {
  PAGE_TITLE_SEPARATOR,
  BOXSET_ID,
  CYW_ID,
  CMS_PROGROW_TYPE_PROGRAMMESET,
  CMS_PROGROW_TYPE_CATEGORY,
} from '../constants';
import ProgrammeRows from './ProgrammeRows';
import { device } from '../v2/Theme';
import { Button as ButtonV2 } from '../v2/components/button/Button';
import { HeroCard as HeroCardV2 } from '../v2/components/hero/heroCard/HeroCard';
import PromotionalCyw from './PromotionalCyw';
import SimpleBarReact from 'simplebar-react';
import { CardGridLoading } from '../v2/components/loading/CardGridLoading';
import { rgba } from 'polished';
import Tag from './Tag';

function Categories({
  curLang,
  strings,
  channels,
  catProgRows,
  catProgRowsIsLoading,
  catProgRowsHasErrored,
  catProgIsLoading,
  catProgHasErrored,
  auth,
  fetchChannels,
  fetchCats,
  progRows,
  catData,
  catProgrammes,
  categories,
  match,
  location,
  history,
}) {
  const [items, setItems] = useState([]);
  const [catID, setCatID] = useState(0);
  const [catSelected, setCatSelected] = useState(false);
  const [pathname, setPathname] = useState('');
  const [width, setWidth] = useState(window.innerWidth);
  const { availability, accessibility, categories: genres } = categories;
  const path = match.path;
  const signed = parseInt(catID, 10) === 100 ? 1 : 0;
  const isCyw = isGenreMatch(catID, CYW_ID);
  const isWelsh = parseInt(curLang, 10) === 0 ? true : false;

  const selectedChannel = getSelectedChannel(catID, channels);

  const updateDimensions = useCallback(() => {
    setWidth(window.innerWidth);
  }, []);

  const getCatProgrammes = useCallback(
    (id) => {
      setCatID(id);
      setCatSelected(true);
      progRows(id);
      catData(id);
    },
    [catData, progRows]
  );

  const checkURL = useCallback(() => {
    if (pathname !== location.pathname) {
      const catArray = location.pathname.split('/');

      if (catArray.length > 2 && catArray[catArray.length - 1] !== '') {
        getCatProgrammes(catArray[catArray.length - 1]);
      } else {
        progRows();
        setCatID(0);
        setCatSelected(false);
      }
      setPathname(location.pathname);
    }
  }, [getCatProgrammes, location.pathname, pathname, progRows]);

  const createItems = useCallback(() => {
    if (JSON.stringify(items) !== JSON.stringify(catProgrammes)) {
      setItems(catProgrammes);
    }
  }, [catProgrammes, items]);

  useEffect(() => {
    fetchChannels();
    fetchCats();
    window.scrollTo(0, 0);
    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [fetchChannels, fetchCats, catSelected, updateDimensions]);

  useEffect(() => {
    createItems(catProgrammes);
    checkURL();

    const activeCat = document.querySelector('.category-nav-active');
    if (activeCat && width < 768) {
      activeCat.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  }, [catProgrammes, checkURL, createItems, width]);

  return (
    <CategoriesContainer className="main">
      <PageMeta
        title={
          catSelected
            ? `${
                strings.categories
              } ${PAGE_TITLE_SEPARATOR} ${getCurrentCategoryName(
                catID,
                categories,
                strings
              )}`
            : strings.categories
        }
        socialTitle={`${getCurrentCategoryName(catID, categories, strings)}`}
        description={
          selectedChannel
            ? selectedChannel.description
            : strings.metaDescriptionCategories
        }
      />
      <CategoriesInner isCyw={isCyw}>
        {isCyw && (
          <CywPlayer>
            <Logo
              src={selectedChannel.logoImage && selectedChannel.logoImage.url}
              alt={selectedChannel.title}
            />
            <PromotionalCyw />
          </CywPlayer>
        )}
        {!isCyw && selectedChannel && (
          //  WIP update selectedChannel.iconImage to be selectedChannel.logoImage when we got Light/Dark Logo in CMS
          <HeroCardV2
            items={catProgRows.heroCards}
            noImage={true}
            logo={selectedChannel.iconImage && selectedChannel.iconImage.url}
            logoAlt={selectedChannel.title}
            auth={auth}
            loading={catProgRowsIsLoading ? true : false}
            isCategory
            history={history}
          />
        )}
        <PageTitle title={strings.allCategories} type="cyw" />
        <CategoriesContent isCyw={isCyw}>
          <SimpleBarReact style={{ width: '100%' }}>
            <CategoryNavigation>
              {genres &&
                Object.keys(genres).length > 0 &&
                Object.keys(genres).map(
                  (key) =>
                    !isGenreMatch(genres[key].id, BOXSET_ID) && (
                      <CategoryNavigationItem
                        key={key}
                        className={
                          location.pathname === path + '/' + genres[key].id &&
                          'category-nav-active'
                        }
                        active={
                          location.pathname === path + '/' + genres[key].id
                        }
                      >
                        <ButtonV2
                          link={path + '/' + genres[key].id}
                          label={genres[key].genre}
                          size="small"
                          type="secondary"
                        />
                      </CategoryNavigationItem>
                    )
                )}
              {availability &&
                Object.keys(availability).length > 0 &&
                Object.keys(availability).map((key) => (
                  <CategoryNavigationItem
                    key={key}
                    className={
                      location.pathname === path + '/' + availability[key].id &&
                      'category-nav-active'
                    }
                    active={
                      location.pathname === path + '/' + availability[key].id
                    }
                  >
                    <ButtonV2
                      key={key}
                      link={path + '/' + availability[key].id}
                      label={availability[key].genre}
                      size="small"
                      type="secondary"
                    />
                  </CategoryNavigationItem>
                ))}
              {accessibility &&
                Object.keys(accessibility).length > 0 &&
                Object.keys(accessibility).map((key) => (
                  <CategoryNavigationItem
                    key={key}
                    className={
                      location.pathname ===
                        path + '/' + accessibility[key].id &&
                      'category-nav-active'
                    }
                    active={
                      location.pathname === path + '/' + accessibility[key].id
                    }
                  >
                    <ButtonV2
                      key={key}
                      link={path + '/' + accessibility[key].id}
                      label={accessibility[key].genre}
                      size="small"
                      type="secondary"
                    />
                  </CategoryNavigationItem>
                ))}
            </CategoryNavigation>
          </SimpleBarReact>

          {!catProgRowsHasErrored &&
            catProgRows.programmeRows &&
            catProgRows.programmeRows.length > 0 && (
              <CategoriesProgrammes>
                {catProgRowsIsLoading && <CardGridLoading />}

                {catProgRows &&
                  catProgRows.programmeRows &&
                  catProgRows.programmeRows.length > 0 &&
                  catProgRows.programmeRows.map(
                    (element, index) =>
                      (element.type !== CMS_PROGROW_TYPE_PROGRAMMESET ||
                        (element.type === CMS_PROGROW_TYPE_PROGRAMMESET &&
                          element.cards &&
                          element.cards.length > 0)) && (
                        <div key={index}>
                          <SectionTitle>
                            {element.type === CMS_PROGROW_TYPE_CATEGORY &&
                              isWelsh &&
                              `${strings.programmes} `}
                            {decodeHtml(element.title)}
                            {element.type === CMS_PROGROW_TYPE_CATEGORY &&
                              !isWelsh &&
                              ` ${strings.programmes.toLowerCase()}`}
                          </SectionTitle>
                          <ProgrammeRows
                            programmeRow={element}
                            index={index}
                            homeLoading={catProgRowsIsLoading}
                            homeError={catProgRowsHasErrored}
                            signed={signed}
                            forceCardGrid={true}
                          />
                        </div>
                      )
                  )}
              </CategoriesProgrammes>
            )}
          {catSelected && (
            <CategoriesProgrammes errored={catProgHasErrored}>
              <SectionTitleContainer>
                <SectionTitle type="categories">
                  {isWelsh && `${strings.programmes} `}
                  {getCurrentCategoryName(catID, categories, strings)}
                  {!isWelsh && ` ${strings.programmes.toLowerCase()}`}
                </SectionTitle>
                {Object.keys(items).length > 0 && (
                  <Tag>
                    {Object.keys(items).length}
                    {Object.keys(items).length > 1
                      ? strings.programmesAvailable
                      : strings.programmeAvailable}
                  </Tag>
                )}
              </SectionTitleContainer>
              {catProgIsLoading && <CardGridLoading />}
              {!catProgIsLoading && (
                <CardGrid
                  items={items}
                  itemsHasErrored={catProgHasErrored}
                  signed={signed}
                  gridType="no-padding"
                />
              )}
            </CategoriesProgrammes>
          )}
        </CategoriesContent>
      </CategoriesInner>
    </CategoriesContainer>
  );
}

const CategoriesContainer = styled.div`
  margin: 4px 0 0 0;
  padding: 149px 0 64px;
`;

const CenteredContent = styled.div`
  margin: auto;
  max-width: 1595px;
`;

const CategoriesInner = styled(CenteredContent)`
  ${(props) => props.isCyw && `max-width: 1440px;`}
  padding: 0;
`;

const CywPlayer = styled.div`
  padding: 0;
  img {
    display: block;
    margin: 0 auto;
    padding: 10px 0 15px;
    max-width: 99px;
    @media screen and (min-width: 768px) {
      margin: initial;
      padding: 3em 0 1.3em;
      max-width: 193px;
    }
    @media screen and (min-width: 1024px) {
      max-width: none;
    }
  }
  @media screen and (min-width: 768px) {
    position: relative;
    padding: 24px 15px 0 0;
  }
`;

const CategoriesContent = styled.div`
  padding: 0 15px;
  @media screen and (min-width: 768px) {
    padding-left: 0;
  }
  @media screen and (min-width: 1920px) {
    margin: auto;
    max-width: 1595px;
    ${(props) => props.isCyw && `max-width: 1440px;`};
  }
`;

const CategoryNavigation = styled.div`
  display: flex;
  flex-direction: row;

  &:focus-visible {
    outline: none;
  }

  > div {
    margin-right: 12px;
    margin-bottom: 14px;
  }

  a {
    white-space: nowrap;
  }

  @media ${device.tablet} {
    flex-wrap: wrap;
    margin-bottom: 1.5em;
  }
`;

const CategoryNavigationItem = styled.div`
  display: inline-block;
  list-style: none;
  margin: 0;
  padding: 0;

  ${(props) =>
    props.active &&
    `
    a {
      border: none;
      background: linear-gradient(266.92deg, #fabb54 0%, ${
        props.theme.color.primaryFull
      } 100%);
      box-shadow: 0px 2px 4px rgba(110, 79, 28, 0.1),
        0px 8px 16px ${
          props.theme.name === 'light'
            ? rgba(props.theme.color.primaryFull, 0.15)
            : rgba(props.theme.color.primaryFull, 0.2)
        };
      span {
        color: ${rgba(props.theme.color.blackFull, 0.8)}!important;
      }
      &:hover {
        border: none;
        background: ${props.theme.color.primaryFull};
        box-shadow: 0px 4px 8px rgba(110, 79, 28, 0.2),
        0px 16px 32px -8px ${
          props.theme.name === 'light'
            ? rgba(props.theme.color.primaryFull, 0.3)
            : props.theme.color.primary50
        };
        span {
          color: ${props.theme.color.blackFull};
        }
      }
    }
    
  `};
`;

const CategoriesProgrammes = styled.div`
  ${(props) =>
    props.errored &&
    `&:last-of-type div {
    margin-left: 0;
    margin-right: 0;
  }
`};
`;

const SectionTitleContainer = styled.div`
  display: flex;
  padding: 1em 0 0.5em;
  flex-wrap: wrap;
  align-items: center;
`;

const Logo = styled.img`
  width: 216px;
`;

const mapStateToProps = (state) => {
  return {
    curLang: state.curLang,
    categories: state.categories.success,
    catIsLoading: state.categoriesIsLoading,
    catHasErrored: state.categoriesHasErrored,
    catProgrammes: state.catProgrammes.success,
    catProgIsLoading: state.catProgrammes.loading,
    catProgHasErrored: state.catProgrammes.error,
    auth: state.auth.authenticated,
    channels: state.channels.success,
    channelsIsLoading: state.channels.loading,
    channelsHasErrored: state.channels.error,
    catProgRows: state.catProgRows.success,
    catProgRowsIsLoading: state.catProgRows.loading,
    catProgRowsHasErrored: state.catProgRows.error,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchCats: () => dispatch(fetchCategories()),
    catData: (id) => dispatch(catFetchData(id)),
    fetchChannels: () => dispatch(channelsFetchData()),
    progRows: (id) => dispatch(catProgRowsFetchData(id)),
  };
};

export default translate(
  connect(mapStateToProps, mapDispatchToProps)(Categories)
);
